import { UUID } from './UUID';
import { FetchType } from './FetchType';
import { BlockType, ElementType } from './ElementType';
import { isNullOrUndefined } from '../../lib/typeGuards';

export type BlockTypes = keyof typeof ElementType | (typeof BlockType)[keyof typeof BlockType];
export type FetchTypes = keyof typeof FetchType;

export interface CommonBlock<
  FetchType extends FetchTypes = FetchTypes,
  BlockType extends BlockTypes = BlockTypes,
> extends UUID {
  fetchUrl: string;
  elementType: BlockType;
  fetchType: FetchType;
  blockLayout?: string;
}

export interface InstantBlock<E, BlockType extends BlockTypes> extends CommonBlock<FetchType.instant, BlockType> {
  embedded: E;
}

export type LazyBlock<BlockType extends BlockTypes> = CommonBlock<FetchType.lazy, BlockType>;

export interface FetchError extends CommonBlock {
  fetchError: true;
  requestId: string;
  errorType?: string;
}

export interface DynamicallyGeneratedBlockContent extends FetchError {
  errorType: 'dynamicallyGeneratedBlockContent';
}

export function isFetchError(block: unknown): block is FetchError {
  return (block as FetchError)?.fetchError;
}

export function isDynamicallyGeneratedBlockContent(block: any): block is DynamicallyGeneratedBlockContent {
  return block?.fetchError && block?.errorType === 'dynamicallyGeneratedBlockContent';
}

export type Block<EmbeddedType = Record<string, unknown>, BlockType extends BlockTypes = BlockTypes> =
  InstantBlock<EmbeddedType, BlockType> | LazyBlock<BlockType>;

export interface Embedded<BlockType extends BlockTypes> {
  elementType: BlockType;
}

export function BlockGuard<T>(elementType: BlockTypes) {
  return (block: unknown): block is T => {
    return (block as any).elementType === elementType;
  };
}

export type EmptyBlock<T extends Block> = T extends { elementType: infer BlockType }
  ? BlockType extends BlockTypes
    ? Block<null, BlockType>
    : never
  : never;

// Describes a block that might come with an embedded === null
export type MaybeEmpty<TBlock extends Block> = TBlock extends Block<any, any> ? TBlock | EmptyBlock<TBlock> : never;

export function isEmptyBlock<TBlock extends Block<any, any>>(
  block: TBlock | EmptyBlock<TBlock>
): block is EmptyBlock<TBlock> {
  return block.fetchType === FetchType.instant && isNullOrUndefined(block.embedded);
}

type NonEmptyBlock<TBlock> = TBlock extends { embedded: null } ? never : TBlock;

export function isNonEmptyBlock<TBlock extends Block<any, any>>(
  block: TBlock | EmptyBlock<TBlock>
): block is NonEmptyBlock<TBlock> {
  return !isEmptyBlock(block);
}
